export type TrackedPromise<T> = Promise<T> & {
	isResolved: () => boolean;
	isRejected: () => boolean;
	isSettled: () => boolean;
	getValue: () => T | undefined;
};
/**
 * Tracks the state of a promise (resolved, rejected, or pending) and caches its result.
 * @param promise The promise to be tracked
 * @returns The tracked promise with additional methods to check its state and access the cached result
 */
export function trackPromise<T>(promise: Promise<T>): TrackedPromise<T> {
	let isResolved = false;
	let isRejected = false;
	let cachedValue: T | undefined;
	// Create a new promise that tracks the resolution or rejection
	const trackedPromise = promise.then(
		(value) => {
			isResolved = true;
			cachedValue = value;
			return value;
		},
		(reason) => {
			isRejected = true;
			throw reason;
		},
	) as TrackedPromise<T>;
	// Add methods to check the state
	trackedPromise.isResolved = () => isResolved;
	trackedPromise.isRejected = () => isRejected;
	trackedPromise.isSettled = () => isResolved || isRejected;
	// Add a method to get the cached value
	trackedPromise.getValue = () => cachedValue;
	return trackedPromise;
}

/**
 * Wraps a function that returns a promise and tracks its state
 * @param fn The function that returns a promise
 * @returns A function that returns a tracked promise
 */
export function trackFunctionPromise<T, A extends any[]>(fn: (...args: A) => Promise<T>) {
	return (...args: A): TrackedPromise<T> => {
		const originalPromise = fn(...args);
		return trackPromise(originalPromise);
	};
}
